import { useEffect, useMemo, useState, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { SiteShell } from "@/components/site/SiteShell";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { PlanCards } from "@/components/checkout/PlanCards";
import { DetailsForm, type IntakeDetails } from "@/components/checkout/DetailsForm";
import { AgencyForm } from "@/components/checkout/AgencyForm";
import { OrderSummary } from "@/components/checkout/OrderSummary";
import { StripeEmbeddedCheckout } from "@/components/StripeEmbeddedCheckout";
import { getStripeEnvironment } from "@/lib/stripe";
import {
  listPlans,
  createCheckoutSession,
  type PublicPlan,
} from "@/lib/checkout.functions";
import { buildHead } from "@/lib/seo";

type CheckoutSearch = { plan?: string };

export const Route = createFileRoute("/checkout")({
  validateSearch: (s: Record<string, unknown>): CheckoutSearch => ({
    plan: typeof s.plan === "string" ? s.plan : undefined,
  }),
  head: () =>
    buildHead({
      locale: "en",
      title: "Order your GEO Readiness Audit · ShugertGEO",
      description:
        "Pick a plan, share your website details, and pay securely. Branded GEO audit delivered in hours.",
      path: "/checkout",
      altPath: "/checkout",
      noindex: true,
    }),
  component: CheckoutPage,
});

const GOLD = "#C8A84B";
const EMPTY: IntakeDetails = {
  website_url: "",
  full_name: "",
  email: "",
  company: "",
  industry: "",
  competitors: "",
  target_audience: "",
  notes: "",
};

type Step = "plan" | "details" | "pay";

function CheckoutPage() {
  const { plan: planParam } = Route.useSearch();
  const fetchPlans = useServerFn(listPlans);
  const createSession = useServerFn(createCheckoutSession);

  const { data, isLoading, error } = useQuery({
    queryKey: ["public-plans"],
    queryFn: () => fetchPlans(),
    staleTime: 60_000,
  });

  const plans = data?.plans ?? [];
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [details, setDetails] = useState<IntakeDetails>(EMPTY);
  const [step, setStep] = useState<Step>("plan");

  useEffect(() => {
    if (!plans.length || selectedPlan) return;
    const fromParam =
      planParam && plans.find((p) => p.id === planParam.toLowerCase());
    if (fromParam) {
      setSelectedPlan(fromParam.id);
      setStep("details");
    }
  }, [plans, planParam, selectedPlan]);

  const plan: PublicPlan | undefined = useMemo(
    () => plans.find((p) => p.id === selectedPlan),
    [plans, selectedPlan],
  );

  const isTeardown = plan?.id === "teardown";
  const heroEyebrow = isTeardown ? "Shugert · Performance" : "ShugertGEO · Order";
  const heroTitle = isTeardown
    ? "Get your Performance Teardown"
    : "Get your GEO Readiness Audit";
  const heroIntro = isTeardown
    ? "Three steps, ~2 minutes. Tell us about your store and pay securely. Our team delivers a deep technical audit of your three highest-impact performance issues within 2 business days."
    : "Three steps, ~2 minutes. Pick a plan, tell us about your site, and pay securely. We start the audit as soon as payment clears.";

  const fetchClientSecret = useCallback(async () => {
    if (!plan) throw new Error("No plan selected");
    const res = await createSession({
      data: {
        plan_id: plan.id as "starter" | "pro" | "teardown",
        website_url: details.website_url,
        full_name: details.full_name,
        email: details.email,
        company: details.company,
        industry: details.industry,
        competitors: details.competitors,
        target_audience: details.target_audience,
        notes: details.notes,
        environment: getStripeEnvironment(),
        return_origin: window.location.origin,
      },
    });
    if ("error" in res) throw new Error(res.error);
    return res.clientSecret;
  }, [createSession, plan, details]);

  return (
    <SiteShell>
      <PaymentTestModeBanner />
      <section className="border-b border-border bg-background">
        <div className="mx-auto max-w-5xl px-6 py-10 sm:py-14">
          <p
            className="text-[10px] font-medium uppercase tracking-[0.22em]"
            style={{ color: GOLD }}
          >
            {heroEyebrow}
          </p>
          <h1 className="mt-2 font-display text-3xl font-semibold text-foreground sm:text-4xl">
            {heroTitle}
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            {heroIntro}
          </p>
          <Stepper step={step} />
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-6 py-10 sm:py-14">
          {isLoading && (
            <p className="text-sm text-muted-foreground">Loading plans…</p>
          )}
          {error && (
            <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              Couldn't load plans. Refresh the page or email hello@shugert.com.mx.
            </p>
          )}

          {!isLoading && !error && step === "plan" && (
            <div className="grid gap-8">
              <PlanCards
                plans={plans}
                value={selectedPlan}
                onChange={(id) => setSelectedPlan(id)}
              />
              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={!plan}
                  onClick={() => setStep("details")}
                  className="rounded-full bg-foreground px-6 py-2.5 text-sm font-medium text-background hover:opacity-90 disabled:opacity-40"
                >
                  Continue →
                </button>
              </div>
            </div>
          )}

          {!isLoading && !error && step === "details" && plan && (
            <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
              <div>
                {plan.is_contact_sales ? (
                  <AgencyForm onBack={() => setStep("plan")} />
                ) : (
                  <DetailsForm
                    value={details}
                    onChange={setDetails}
                    onBack={() => setStep("plan")}
                    onSubmit={() => setStep("pay")}
                  />
                )}
              </div>
              <OrderSummary
                plan={plan}
                email={details.email || undefined}
                website={details.website_url || undefined}
              />
            </div>
          )}

          {!isLoading && !error && step === "pay" && plan && (
            <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
              <div className="grid gap-4">
                <button
                  type="button"
                  onClick={() => setStep("details")}
                  className="self-start text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground"
                >
                  ← Edit details
                </button>
                <StripeEmbeddedCheckout fetchClientSecret={fetchClientSecret} />
              </div>
              <OrderSummary
                plan={plan}
                email={details.email || undefined}
                website={details.website_url || undefined}
              />
            </div>
          )}
        </div>
      </section>
    </SiteShell>
  );
}

function Stepper({ step }: { step: Step }) {
  const items: { id: Step; label: string }[] = [
    { id: "plan", label: "1. Plan" },
    { id: "details", label: "2. Details" },
    { id: "pay", label: "3. Payment" },
  ];
  const idx = items.findIndex((i) => i.id === step);
  return (
    <ol className="mt-8 flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.18em] text-muted-foreground">
      {items.map((item, i) => {
        const active = i === idx;
        const done = i < idx;
        return (
          <li key={item.id} className="flex items-center gap-3">
            <span
              className="rounded-full px-3 py-1"
              style={{
                background: active ? GOLD : "transparent",
                color: active ? "#0a0a0a" : done ? "var(--color-foreground)" : undefined,
                border: active ? `1px solid ${GOLD}` : "1px solid var(--color-border)",
              }}
            >
              {item.label}
            </span>
            {i < items.length - 1 && <span aria-hidden>→</span>}
          </li>
        );
      })}
    </ol>
  );
}